// ----------------------------------------------------------------------
// <copyright file="Izenda.DatabaseMapping.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var m = require('mobx');
  var trackable = require('share/tracking/Izenda.Trackable');
  var ObjectStateConstants = require('logic/data/Izenda.ObjectStateConstants');
  var constants = require('logic/data/Izenda.DatabaseMappingConstants');

  const {
    filter,
    isEmpty,
    isNil,
    flow,
    first,
    cloneDeep,
    trim,
    replace,
    uniqueId,
    split,
    indexOf,
    remove,
    difference,
    startsWith
  } = require('lodash/fp');

  function DatabaseMapping(data) {
    var staticProps = {
      version: null,
      id: null
    };

    var observableProps = {
      fromServer: null,
      fromDatabaseName: null,
      optionFromDatabaseNameValue: function() {
        return buildOptionValue(this.fromServer, this.fromDatabaseName);
      },
      optionFromTooltipValue: function() {
        return buildTooltip(this.fromServer, this.fromDatabaseName);
      },
      type: null,
      fromObject: null,
      toServer: null,
      toDatabaseName: null,
      optionToDatabaseNameValue: function() {
        return buildOptionValue(this.toServer, this.toDatabaseName);
      },
      optionToTooltipValue: function() {
        return buildTooltip(this.toServer, this.toDatabaseName);
      },
      toObject: null,
      tenants: [], // Array of tenant ids.
      state: ObjectStateConstants.NONE,
      selectAllTenants: function() {
        var firstTenant = first(this.tenants);
        return this.tenants.length === 1 && firstTenant === constants.ALL_TENANT_ID;
      },
      displayTenants: function() {
        return this.tenants.peek().join();
      },
      hashValue: function() {
        return String.format(
          constants.MAPPING_HASH_PATTERN,
          this.fromServer,
          this.fromDatabaseName,
          this.type,
          this.fromObject,
          this.toServer,
          this.toDatabaseName,
          this.toObject
        );
      },
      message: null,
      showMessage: function() {
        return !isNil(this.message);
      },
      isMappingToGridDisabled: function() {
        return isNil(this.fromDatabaseName) || isNil(this.type) || isNil(this.fromObject);
      }
    };

    m.extendObservable(trackable(this), observableProps);
    Object.assign(this, staticProps);

    data && this.populate(data);
    this.resetDirty();

    /**
     * Build default buildTooltip
     * (Use for lazy loading.)
     * @param {String} serverName
     * @param {String} composedDatabaseName
     */
    function buildTooltip(serverName, composedDatabaseName) {
      if (isNil(serverName) || isNil(composedDatabaseName)) {
        return;
      }
      var regex = new RegExp('\\[\\w*\\]\\s');
      var cloneValue = cloneDeep(composedDatabaseName);
      var matches = regex.exec(cloneValue);

      var serverTypeName = trim(matches[0]);
      var databaseName = replace(matches[0], '', cloneValue);
      return String.format('{0} - {1} - {2}', serverTypeName, serverName, databaseName);
    }

    /**
     * Build option value.
     * @param {String} server
     * @param {String} databaseName
     */
    function buildOptionValue(server, databaseName) {
      return databaseName ? String.format('{0} - {1}', server, databaseName) : null;
    }
  }

  /**
   * Populate data.
   * @param {Object} data
   */
  DatabaseMapping.prototype.populate = m.action(function populate(data) {
    this.id = data.id || uniqueId(constants.ID_PREFIX);
    this.version = data.version;
    this.state = data.state;

    this.fromServer = data.fromServer;
    this.fromDatabaseName = data.fromDatabaseName;
    this.type = data.type || constants.MAPPING_TYPES[0].value;
    this.fromObject = data.fromObject;
    this.toServer = data.toServer;
    this.toDatabaseName = data.toDatabaseName;
    this.toObject = data.toObject;
    this.tenants = data.tenants ? data.tenants : [constants.ALL_TENANT_ID];
  });

  /**
   * Update value of property.
   * @param {String} key
   * @param {Object} value
   */
  DatabaseMapping.prototype.setPropertyValue = m.action(function setPropertyValue(key, value) {
    this[key] = value;
    this.markDirty();
  });

  /**
   * Update Mapping Tenant
   */
  DatabaseMapping.prototype.updateMappingTenant = m.action(function(tenants) {
    var selectedTenants = split(',', tenants);
    var newTenantId = difference(selectedTenants, this.tenants)[0];

    if (newTenantId === constants.ALL_TENANT_ID) {
      this.tenants = [constants.ALL_TENANT_ID];
    } else {
      this.tenants = filter(function(item) {
        return item !== constants.ALL_TENANT_ID;
      }, selectedTenants);
    }

    this.markDirty();
  });

  /**
   * Verify mapping has a specific tenant
   * @param {String} id
   */
  DatabaseMapping.prototype.hasTenant = function(id) {
    var hasAllTenant = indexOf(constants.ALL_TENANT_ID, this.tenants) !== -1;
    var isFirstTenantEmpty = flow(first, isEmpty)(this.tenants);

    if (hasAllTenant || isFirstTenantEmpty) {
      return true;
    }

    return indexOf(id, this.tenants) !== -1;
  };

  /**
   * Verify state with a specific state.
   * @param {String} state
   */
  DatabaseMapping.prototype.checkState = function(state) {
    return this.state === state;
  };

  /**
   * Calculate data for saving.
   */
  DatabaseMapping.prototype.buildDataForSaving = function() {
    var result = {
      id: this.isNew() ? null : this.id,
      fromServer: this.fromServer,
      fromDatabaseName: this.fromDatabaseName,
      type: this.type,
      fromObject: this.fromObject,
      toServer: this.toServer,
      toDatabaseName: this.toDatabaseName,
      toObject: this.toObject,
      tenants: !this.selectAllTenants ? remove(isEmpty, this.tenants) : null,
      state: this.state,
      version: this.version,
      selectAllTenants: this.selectAllTenants
    };

    this.isDirty && this.state === ObjectStateConstants.NONE && (result.state = ObjectStateConstants.UPDATE);
    return result;
  };

  /**
   * Check whether this is a new one.
   */
  DatabaseMapping.prototype.isNew = function() {
    return startsWith(constants.ID_PREFIX, this.id);
  };

  module.exports = DatabaseMapping;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.DatabaseMapping.js